import type { Identifier } from "ra-core";

import { AiCopilotError, toAiErrorCode } from "../../aiCopilot/aiErrors";
import { computeSpendStatus } from "../../aiCopilot/aiUsage";
import type {
  AiCopilotRequest,
  AiCopilotResponse,
  AiDecision,
  AiMonthlyUsage,
  AiSettings,
  AiSettingsUpdate,
  AiSpendStatus,
  DealAiSuggestion,
} from "../../types";
import { getSupabaseClient } from "./supabase";

/**
 * Almikia Epic EI (TASK-003): Supabase-side AI copilot methods. Real calls go
 * ONLY through the `ai_copilot` edge function (which holds the Anthropic key
 * server-side); reads/updates use the new tables/view and are narrowed by
 * their RLS (direccion-only settings/usage, lead-scoped suggestions). No key,
 * secret or model URL exists in this file.
 */

const SETTINGS_ERROR = "No se pudo leer la configuración de IA";
const USAGE_ERROR = "No se pudo leer el uso de IA";

/** Extracts the edge function's `code` from a failed `functions.invoke`. */
const readErrorCode = async (error: unknown): Promise<unknown> => {
  try {
    const context = (error as { context?: { json?: () => Promise<unknown> } })
      ?.context;
    const body = (await context?.json?.()) as { code?: unknown } | undefined;
    return body?.code;
  } catch {
    return undefined;
  }
};

const isCopilotResponse = (value: unknown): value is AiCopilotResponse => {
  const v = value as Partial<AiCopilotResponse> | null;
  return (
    !!v &&
    v.suggestionId != null &&
    typeof v.action === "string" &&
    typeof v.result === "object" &&
    v.result !== null
  );
};

async function readSettings(): Promise<AiSettings> {
  const { data, error } = await getSupabaseClient()
    .from("ai_settings")
    .select("*")
    .maybeSingle();
  if (error || !data) {
    console.error("ai_settings.select.error", error);
    throw new Error(error?.message || SETTINGS_ERROR);
  }
  return data as AiSettings;
}

async function readMonthlyUsage(): Promise<AiMonthlyUsage[]> {
  const { data, error } = await getSupabaseClient()
    .from("ai_usage_monthly")
    .select("*")
    .order("month", { ascending: false });
  if (error) {
    console.error("ai_usage_monthly.select.error", error);
    throw new Error(error.message || USAGE_ERROR);
  }
  return ((data ?? []) as AiMonthlyUsage[]).map((row) => ({
    ...row,
    estimated_cost_usd: Number(row.estimated_cost_usd),
  }));
}

export const createSupabaseAiCopilotMethods = () => ({
  async requestAiSuggestions(
    input: AiCopilotRequest,
  ): Promise<AiCopilotResponse> {
    const { data, error } = await getSupabaseClient().functions.invoke<unknown>(
      "ai_copilot",
      { method: "POST", body: input },
    );
    if (error) {
      // Never log the request body (it can carry lead text); code only.
      const code = await readErrorCode(error);
      console.error("ai_copilot.error", code);
      throw new AiCopilotError(toAiErrorCode(code));
    }
    if (!isCopilotResponse(data)) throw new AiCopilotError("invalid_response");
    return data;
  },

  async decideAiSuggestion(
    id: Identifier,
    decision: AiDecision,
  ): Promise<DealAiSuggestion> {
    const client = getSupabaseClient();
    let payload: DealAiSuggestion["payload"] | undefined;
    if (decision.editedText !== undefined) {
      const { data: current, error: readError } = await client
        .from("deal_ai_suggestions")
        .select("kind, payload")
        .eq("id", id)
        .single();
      if (readError || !current) {
        throw new Error(readError?.message || "No se pudo leer la sugerencia");
      }
      if (current.kind !== "message") {
        throw new Error("Solo se puede editar el texto de un mensaje sugerido");
      }
      payload = { ...current.payload, message: decision.editedText.trim() };
    }
    // decided_at is assigned by the DB guard trigger; only status (+ message text) is sent.
    const { data, error } = await client
      .from("deal_ai_suggestions")
      .update({ status: decision.status, ...(payload ? { payload } : {}) })
      .eq("id", id)
      .select()
      .single();
    if (error || !data) {
      console.error("deal_ai_suggestions.update.error", error);
      throw new Error(error?.message || "No se pudo guardar la decisión");
    }
    return data as DealAiSuggestion;
  },

  async getAiSuggestions(dealId: Identifier): Promise<DealAiSuggestion[]> {
    const { data, error } = await getSupabaseClient()
      .from("deal_ai_suggestions")
      .select("*")
      .eq("deal_id", dealId)
      .order("id", { ascending: false });
    if (error) {
      console.error("deal_ai_suggestions.select.error", error);
      throw new Error(error.message || "No se pudieron leer las sugerencias");
    }
    return (data ?? []) as DealAiSuggestion[];
  },

  getAiSettings: readSettings,

  async updateAiSettings(update: AiSettingsUpdate): Promise<AiSettings> {
    // Direccion-only via RLS; updated_by/updated_at are stamped by a trigger.
    const { data, error } = await getSupabaseClient()
      .from("ai_settings")
      .update(update)
      .eq("id", true)
      .select()
      .single();
    if (error || !data) {
      console.error("ai_settings.update.error", error);
      throw new Error(error?.message || "No se pudo guardar la configuración");
    }
    return data as AiSettings;
  },

  getAiMonthlyUsage: readMonthlyUsage,

  async getAiSpendStatus(): Promise<AiSpendStatus> {
    const [settings, rollup] = await Promise.all([
      readSettings(),
      readMonthlyUsage(),
    ]);
    return computeSpendStatus(rollup, settings);
  },
});
